import { useTranslation } from 'react-i18next';
import {
  MetricIcon,
  MetricItem,
  ProgressCircle,
  ProgressCircleWrap,
  StatusDot,
  StatusLabel,
  StatusRow,
} from '../RouteProgress.styles';
import type { CurrentRouteData } from '../../../../types/CleaningControls.types';
import { StatusIcon } from '../RoutineTimeline/RoutineTimeline.styles';
import {
  Card,
  MetricsRow,
  Results,
  RouteName,
  SkipButton,
  TimeLeft,
  TimeRemaining,
  TimeRemainingLabel,
  TimeRemainingValue,
} from './CurrentRouteCard.styles';
import { PendingSpinnerIcon } from 'lib/components/PendingSpinnerIcon/PendingSpinnerIcon';
import { RobotBatteryPercentIcon } from 'app/modules/cleaning/widgets/robot-dashboard/components/RobotDashboard/RobotBatteryIcon/RobotBatteryIcon';
import { RobotWaterLevelIcon } from 'app/modules/cleaning/widgets/robot-dashboard/components/RobotDashboard/RobotWaterLevel/WaterLevelIcon';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';

interface CurrentRouteCardProps {
  route: CurrentRouteData;
  isPaused?: boolean;
  /** B50 single route run: no route name, and the time left reads as an estimate rather than "min left". */
  isSingleRoute?: boolean;
  showSkip?: boolean;
  isSkipDisabled?: boolean;
  isSkipPending?: boolean;
  onSkipRoute?: () => void;
}

const getStatusLabelKey = (route: CurrentRouteData, isPaused: boolean): string => {
  // Stopped: no route is being cleaned, so the robot's own status is shown.
  if (route.isStopped) {
    return `robotDashboard.${route.robotStatusName}`;
  }

  if (route.isDone) {
    return 'robotCleaningControl.liveMap.done';
  }

  return isPaused ? 'robotDashboard.Paused' : 'robotDashboard.Cleaning';
};

export const CurrentRouteCard = ({
  route,
  isPaused = false,
  isSingleRoute = false,
  showSkip = true,
  isSkipDisabled = false,
  isSkipPending = false,
  onSkipRoute,
}: CurrentRouteCardProps): JSX.Element => {
  const { t } = useTranslation();
  const {
    name: routeName,
    isDone,
    isStopped,
    progressPercent,
    minutesLeft,
    batteryPercent,
    waterLiters,
    waterPercent,
  } = route;
  const isRunning = !isDone && !isStopped;

  return (
    <Card>
      {isRunning ? (
        <ProgressCircleWrap>
          <ProgressCircle percent={progressPercent} size={40} strokeWidth={8} />
        </ProgressCircleWrap>
      ) : (
        <StatusIcon $tone={isStopped ? 'muted' : 'success'}>
          <SvgIcon name={isStopped ? 'house' : 'check'} size={24} />
        </StatusIcon>
      )}
      <Results>
        <StatusRow>
          <StatusDot $isDone={isDone} $isMuted={isStopped} $robotStatus={route.robotStatusName} />
          <StatusLabel>{t(getStatusLabelKey(route, isPaused))}</StatusLabel>
        </StatusRow>
        {!isSingleRoute && !isStopped && <RouteName title={routeName}>{routeName}</RouteName>}
        <MetricsRow>
          <MetricItem>
            <MetricIcon className="metric-icon--battery">
              <RobotBatteryPercentIcon batteryPercentageNumber={batteryPercent} size={20} />
            </MetricIcon>
            {batteryPercent}%
          </MetricItem>
          {waterLiters !== undefined && (
            <MetricItem>
              <MetricIcon>
                <RobotWaterLevelIcon waterPercent={waterPercent ?? 0} size={20} />
              </MetricIcon>
              {t('robotCleaningControl.liveMap.waterLiters', { liters: waterLiters })}
            </MetricItem>
          )}
        </MetricsRow>
      </Results>
      {isRunning && minutesLeft !== undefined && isSingleRoute && (
        <TimeRemaining>
          <TimeRemainingValue>
            {t('robotCleaningControl.liveMap.minutesShort', { minutes: minutesLeft })}
          </TimeRemainingValue>
          <TimeRemainingLabel>{t('robotCleaningControl.liveMap.estimatedTimeRemaining')}</TimeRemainingLabel>
        </TimeRemaining>
      )}
      {isRunning && minutesLeft !== undefined && !isSingleRoute && (
        <TimeLeft>{t('robotCleaningControl.liveMap.minutesLeft', { count: minutesLeft })}</TimeLeft>
      )}
      {showSkip && isRunning && (
        <SkipButton
          aria-label={t('robotCleaningControl.skipRoute.label')}
          title={t('robotCleaningControl.skipRoute.label')}
          disabled={isSkipDisabled}
          icon={isSkipPending ? <PendingSpinnerIcon /> : <SvgIcon name="skip" size={20} />}
          onClick={onSkipRoute}
        />
      )}
    </Card>
  );
};
